<template>
  <div
    ref="rowRef"
    class="dropdown-row"
    :class="{ highlight: activeIndex === index, disabled }"
    @click="emits('onClicked')"
    @mouseenter="(e) => onHover(e, index, title, subtitle)"
  >
    <p>
      {{ title
      }}<span v-if="subtitle?.length" class="dropdown-row__subtitle"> ({{ subtitle }})</span>
    </p>
    <IconCheck v-show="selected" class="dropdown-row__icon" />
  </div>
</template>
<script lang="ts" setup>
import { toRefs, ref, watch } from 'vue'
import IconCheck from '@/design-system/assets/icons/IconCheck.vue'

interface EMITS {
  (e: 'onClicked')
  (e: 'onHover', event: MouseEvent, index: number, title: string, subtitle?: string | undefined)
}
const emits = defineEmits<EMITS>()
interface Props {
  activeIndex: number
  disabled: boolean
  index: number
  lastNavigationWay: string
  selected?: boolean
  subtitle?: string | undefined
  title: string
}
const props = defineProps<Props>()
const {
  activeIndex,
  disabled = false,
  index,
  lastNavigationWay,
  selected,
  subtitle,
  title
} = toRefs(props)
const rowRef = ref<HTMLElement | null>(null)
function onHover(event: MouseEvent, index: number, title: string, subtitle?: string | undefined) {
  emits('onHover', event, index, title, subtitle)
}
watch(activeIndex, (newActiveIndex) => {
  if (newActiveIndex === index.value && lastNavigationWay.value !== 'hover')
    rowRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.dropdown-row {
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  gap: variables.$gap-small;
  height: variables.$dropdown-row-height;
  justify-content: space-between;
  padding: variables.$padding-medium variables.$padding-xlarge;
  &__icon {
    color: variables.$color-primary;
    min-width: 24px;
  }
  & p {
    color: variables.$color-text-primary;
    @include font-mixins.desktop-regular-label;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  &__subtitle {
    color: variables.$color-text-secondary;
  }
  &.disabled {
    cursor: not-allowed;
    background: variables.$color-pure-white;
    color: variables.$color-greyscale-secondary;
    & p {
      color: variables.$color-text-secondary;
    }
  }
  &:hover,
  &.highlight {
    background: variables.$color-primary;
    .dropdown-row__icon {
      color: variables.$color-pure-white;
    }
    & p {
      color: variables.$color-pure-white;
      .dropdown-row__subtitle {
        color: variables.$color-greyscale-light;
      }
    }
  }
  &.disabled {
    background: variables.$color-pure-white;
    & p {
      color: variables.$color-text-secondary;
      .dropdown-row__subtitle {
        color: variables.$color-text-secondary;
      }
    }
  }
}
.hover-explanation {
  position: absolute;
  left: 10px;
  max-width: 240px;
  height: fit-content;
  z-index: 10;
  text-align: center;
  border-radius: variables.$border-radius-small;
  padding: variables.$padding-large variables.$padding-xlarge;
  background-color: variables.$color-greyscale-content;
  &-text {
    @include font-mixins.desktop-regular-label;
    color: variables.$color-pure-white;
    margin: 0;
    width: 208px;
    height: fit-content;
  }
}
</style>
